import { useState, useEffect, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";

// Pages
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import PatientsPage from "@/pages/PatientsPage";
import PatientDetailPage from "@/pages/PatientDetailPage";
import PTATestPage from "@/pages/PTATestPage";
import SpeechTestPage from "@/pages/SpeechTestPage";
import TympanometryPage from "@/pages/TympanometryPage";
import RecommendationsPage from "@/pages/RecommendationsPage";
import ClinicsPage from "@/pages/ClinicsPage";
import CatalogPage from "@/pages/CatalogPage";
import AppointmentsPage from "@/pages/AppointmentsPage";
import AnalyticsPage from "@/pages/AnalyticsPage";
import AuditLogsPage from "@/pages/AuditLogsPage";
import UsersPage from "@/pages/UsersPage";
import TeleAudiologyPage from "@/pages/TeleAudiologyPage";
import TrialsPage from "@/pages/TrialsPage";
import LeadsPage from "@/pages/LeadsPage";
import ReportsPage from "@/pages/ReportsPage";
import ZohoIntegrationPage from "@/pages/ZohoIntegrationPage";
import ClusterManagementPage from "@/pages/ClusterManagementPage";
import PTALiveTestPage from "@/pages/PTALiveTestPage";
import PTAAudiologistPage from "@/pages/PTAAudiologistPage";
import PTAPatientPage from "@/pages/PTAPatientPage";
import SelfTestPage from "@/pages/SelfTestPage";
import SelfTestManagementPage from "@/pages/SelfTestManagementPage";
import LandingPage from "@/pages/LandingPage";
import CustomerPortalPage from "@/pages/CustomerPortalPage";
import LiveMonitorPage from "@/pages/LiveMonitorPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};

// Auth Provider Component
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("hearclear_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verifyToken = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setUser(response.data);
        } catch (error) {
          console.error("Token verification failed:", error);
          localStorage.removeItem("hearclear_token");
          setToken(null);
          setUser(null);
        }
      }
      setLoading(false);
    };
    verifyToken();
  }, [token]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      localStorage.setItem("hearclear_token", access_token);
      setToken(access_token);
      setUser(userData);
      toast.success(`Welcome back, ${userData.name}!`);
      return { success: true };
    } catch (error) {
      const message = error.response?.data?.detail || "Login failed";
      toast.error(message);
      return { success: false, error: message };
    }
  };

  const logout = () => {
    localStorage.removeItem("hearclear_token");
    setToken(null);
    setUser(null);
    toast.info("You have been logged out");
  };

  const value = {
    user,
    token,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    isSuperAdmin: user?.role === "super_admin",
    isClinicAdmin: user?.role === "clinic_admin",
    isAudiologist: user?.role === "audiologist",
    isSalesCRM: user?.role === "sales_crm"
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Protected Route Component
const ProtectedRoute = ({ children, allowedRoles = [] }) => {
  const { isAuthenticated, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-sky-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles.length > 0 && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// API Client with auth
export const apiClient = axios.create({
  baseURL: API,
});

apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem("hearclear_token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem("hearclear_token");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

function App() {
  return (
    <AuthProvider>
      <div className="App">
        <Toaster position="top-right" richColors closeButton />
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <DashboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/patients"
              element={
                <ProtectedRoute>
                  <PatientsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/patients/:patientId"
              element={
                <ProtectedRoute>
                  <PatientDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pta-test"
              element={
                <ProtectedRoute allowedRoles={["super_admin", "audiologist"]}>
                  <PTATestPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pta-test/:patientId"
              element={
                <ProtectedRoute allowedRoles={["super_admin", "audiologist"]}>
                  <PTATestPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/speech-test"
              element={
                <ProtectedRoute allowedRoles={["super_admin", "audiologist"]}>
                  <SpeechTestPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/speech-test/:patientId"
              element={
                <ProtectedRoute allowedRoles={["super_admin", "audiologist"]}>
                  <SpeechTestPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tele-audiology"
              element={
                <ProtectedRoute allowedRoles={["super_admin", "clinic_admin", "audiologist"]}>
                  <TeleAudiologyPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/recommendations"
              element={
                <ProtectedRoute>
                  <RecommendationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clinics"
              element={
                <ProtectedRoute allowedRoles={["super_admin"]}>
                  <ClinicsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/catalog"
              element={
                <ProtectedRoute>
                  <CatalogPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/appointments"
              element={
                <ProtectedRoute>
                  <AppointmentsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <ProtectedRoute allowedRoles={["super_admin", "clinic_admin"]}>
                  <AnalyticsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/audit-logs"
              element={
                <ProtectedRoute allowedRoles={["super_admin"]}>
                  <AuditLogsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute allowedRoles={["super_admin", "clinic_admin"]}>
                  <UsersPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tympanometry/:patientId"
              element={
                <ProtectedRoute allowedRoles={["super_admin", "audiologist", "head"]}>
                  <TympanometryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/trials"
              element={
                <ProtectedRoute>
                  <TrialsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/leads"
              element={
                <ProtectedRoute>
                  <LeadsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <ProtectedRoute allowedRoles={["super_admin", "head", "clinic_admin"]}>
                  <ReportsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/zoho"
              element={
                <ProtectedRoute allowedRoles={["super_admin", "clinic_admin"]}>
                  <ZohoIntegrationPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/clusters"
              element={
                <ProtectedRoute allowedRoles={["super_admin", "head"]}>
                  <ClusterManagementPage />
                </ProtectedRoute>
              }
            />
            {/* Live PTA Test Routes */}
            <Route
              path="/pta-live"
              element={
                <ProtectedRoute allowedRoles={["super_admin", "audiologist", "clinic_admin"]}>
                  <PTALiveTestPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/live-monitor"
              element={
                <ProtectedRoute allowedRoles={["super_admin", "audiologist", "clinic_admin"]}>
                  <LiveMonitorPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/pta-test/audiologist/:sessionId"
              element={
                <ProtectedRoute allowedRoles={["super_admin", "audiologist", "clinic_admin"]}>
                  <PTAAudiologistPage />
                </ProtectedRoute>
              }
            />
            {/* Patient-facing PTA test page (no auth required) */}
            <Route
              path="/pta-test/patient/:sessionCode"
              element={<PTAPatientPage />}
            />
            {/* Self-Test Management (staff) */}
            <Route
              path="/test-sessions"
              element={
                <ProtectedRoute allowedRoles={["super_admin", "audiologist", "clinic_admin", "sales_crm", "front_desk"]}>
                  <SelfTestManagementPage />
                </ProtectedRoute>
              }
            />
            {/* Self-Test Public Page (patient) */}
            <Route
              path="/self-test/:testCode"
              element={<SelfTestPage />}
            />
            {/* Customer Portal */}
            <Route path="/my-tests" element={<CustomerPortalPage />} />
            {/* Public Landing Page */}
            <Route path="/" element={<LandingPage />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </BrowserRouter>
      </div>
    </AuthProvider>
  );
}

export default App;
